import axios from 'axios';
import { ThemeId, DEFAULT_THEME_ID, isThemeId } from '../theme/themes';
import { Hours } from '../branch/hours';

export interface ShopInfo {
  addressText?: string;
  googleMapLink?: string;
  phoneNumber?: string;
  phoneLink?: string;
  websiteLink?: string;
  contactLink?: string;
  fbLink?: string;
  fbText?: string;
  instaLink?: string;
  instaText?: string;
  googleReviewLink?: string;
  wifiLink?: string;
  loyaltyLink?: string;
  infoText?: string;
  efoodLink?: string;
  woltLink?: string;
  boxLink?: string;
  logoUrl?: string;
  heroUrl?: string;
  popupUrl?: string;
  /** Optional welcome video (https, mp4); a branch may override the brand's. */
  heroVideoUrl?: string;
  /** schema.org type for JSON-LD (Restaurant | CafeOrCoffeeShop | ...). */
  businessType?: string;
  /** Display name of the shop/brand, when the owner has set it. */
  shopName?: string;
}

/** A branch as listed on the brand picker (`GET /settings` → `branches`, active only, unsorted). */
export interface BranchSummary {
  slug: string;
  name: string;
  /** Optional English display name (e.g. "Kolonaki"). */
  nameEn?: string;
  addressText?: string;
  googleMapLink?: string;
  hours?: Hours;
  lat?: number;
  lng?: number;
}

/** The branch the guest is in (`GET /settings?branch=` → `branch`). Its contact info is merged into `info`. */
export interface BranchDetail {
  slug: string;
  name: string;
  nameEn?: string;
  country?: string;
  hours?: Hours;
  lat?: number;
  lng?: number;
}

export interface ShopSettings {
  themeId: ThemeId;
  brandColor?: string;
  accentColor?: string;
  info: ShopInfo;
  status?: string;
  /** Active branches of a franchise brand; [] for shops without branches. */
  branches: BranchSummary[];
  /** The requested branch, or null (no candidate, unknown or inactive). */
  branch: BranchDetail | null;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const isBranchLike = (b: unknown): b is { slug: string; name: string } =>
  !!b && typeof b === 'object' && typeof (b as { slug?: unknown }).slug === 'string' &&
  typeof (b as { name?: unknown }).name === 'string';

/**
 * `branch` is the path's branch candidate. Without one the request is exactly today's
 * (no query string), so shops without branches see no change.
 */
export async function fetchSettings(branch?: string | null): Promise<ShopSettings> {
  try {
    const res = await axios.get<{
      themeId?: string;
      brandColor?: string;
      accentColor?: string;
      theme?: Record<string, unknown>;
      info?: ShopInfo;
      status?: string;
      branches?: unknown;
      branch?: unknown;
    }>(`${API_BASE_URL}/settings${branch ? `?branch=${encodeURIComponent(branch)}` : ''}`);
    const data = res.data || {};
    const themeId: ThemeId = isThemeId(data.themeId) ? data.themeId : DEFAULT_THEME_ID;
    return {
      themeId,
      brandColor: data.brandColor,
      accentColor: data.accentColor,
      info: data.info || {},
      status: data.status,
      branches: Array.isArray(data.branches) ? (data.branches.filter(isBranchLike) as BranchSummary[]) : [],
      branch: isBranchLike(data.branch) ? (data.branch as BranchDetail) : null,
    };
  } catch (err) {
    console.error('[fetchSettings] failed, using defaults', err);
    return { themeId: DEFAULT_THEME_ID, info: {}, status: 'active', branches: [], branch: null };
  }
}
